import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import Header from "@/components/header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useState, useEffect } from "react";
import { useTitle } from "@/hooks/use-title";
import { useTranslation } from "@/i18n/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Plus, Pencil, Trash } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface Hint {
  id: number;
  content: string;
  price: number;
  customPuzzleId?: number;
  puzzleId?: number;
  createdAt: string;
}

interface User {
  id: number;
  email: string;
  cacatais: number;
  [key: string]: any;
}

interface Puzzle {
  id: number;
  title: string;
  description: string;
  address: string;
  userId: number;
}

const hintFormSchema = z.object({
  content: z.string().min(1, "Content is required"),
  price: z.number()
    .min(1, "Price must be greater than 0")
    .max(9999.99, "Price cannot exceed 9999.99 cacatais")
});

type HintFormData = z.infer<typeof hintFormSchema>;

export default function PuzzleHintsPage() {
  const params = useParams<{ id: string }>();
  const puzzleId = parseInt(params.id);
  const { t, formatNumber } = useTranslation();
  const { toast } = useToast();
  const [_, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [editingHintId, setEditingHintId] = useState<number | null>(null);

  // Fetch puzzle details
  const { data: puzzle, isLoading: isPuzzleLoading } = useQuery<Puzzle>({
    queryKey: [`/api/puzzles/custom/${puzzleId}`],
    enabled: !isNaN(puzzleId)
  });

  // Fetch current user
  const { data: user, isLoading: isUserLoading } = useQuery<User>({
    queryKey: ["/api/auth/user"],
  });

  // Fetch hints for the puzzle
  const { data: hints = [], isLoading: isHintsLoading } = useQuery<Hint[]>({
    queryKey: [`/api/puzzles/custom/${puzzleId}/hints`],
    enabled: !isNaN(puzzleId) && !!puzzle && !!user && user.id === puzzle.userId
  });

  useTitle(puzzle ? `${t("puzzles.manageHints")} - ${puzzle.title}` : t("puzzles.manageHints"));

  const form = useForm<HintFormData>({
    resolver: zodResolver(hintFormSchema),
    defaultValues: {
      content: "",
      price: 100,
    },
  });

  // Set form values when editing a hint
  useEffect(() => {
    if (editingHintId && hints) {
      const hint = hints.find(h => h.id === editingHintId);
      if (hint) {
        form.reset({
          content: hint.content,
          price: hint.price,
        });
      }
    }
  }, [editingHintId, hints, form]);

  // Cancel editing and reset form
  const cancelEditing = () => {
    setEditingHintId(null);
    form.reset({
      content: "",
      price: 100,
    });
  };

  // Create a new hint
  const createHintMutation = useMutation({
    mutationFn: async (data: HintFormData) => {
      const res = await fetch(`/api/puzzles/custom/${puzzleId}/hints`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to create hint");
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/puzzles/custom/${puzzleId}/hints`] });
      toast({ title: t("puzzles.hintCreated") });
      form.reset({
        content: "",
        price: 100,
      });
    },
    onError: (error) => {
      toast({
        title: t("puzzles.hintCreateError"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update an existing hint
  const updateHintMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: HintFormData }) => {
      const res = await fetch(`/api/puzzles/custom/${puzzleId}/hints/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to update hint");
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/puzzles/custom/${puzzleId}/hints`] });
      toast({ title: t("puzzles.hintUpdated") });
      setEditingHintId(null);
      form.reset({
        content: "",
        price: 100,
      });
    },
    onError: (error) => {
      toast({
        title: t("puzzles.hintUpdateError"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete a hint
  const deleteHintMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/puzzles/custom/${puzzleId}/hints/${id}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to delete hint");
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/puzzles/custom/${puzzleId}/hints`] });
      toast({ title: t("puzzles.hintDeleted") });
    },
    onError: (error) => {
      toast({
        title: t("puzzles.hintDeleteError"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: HintFormData) => {
    if (editingHintId) {
      updateHintMutation.mutate({ id: editingHintId, data });
    } else {
      createHintMutation.mutate(data);
    }
  };

  if (isPuzzleLoading || isUserLoading || isHintsLoading) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto pt-24 px-6 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // Check if user is authorized to manage this puzzle
  if (!user || !puzzle || user.id !== puzzle.userId) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto pt-24 px-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>{t("auth.notAuthorized")}</CardTitle>
              <CardDescription>{t("puzzles.notOwner")}</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <div className="container mx-auto pt-24 px-6">
        <Card className="bg-zinc-900 border-zinc-800 mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t("puzzles.manageHints")}</CardTitle>
                <CardDescription>{puzzle.title}</CardDescription>
              </div>
              <Button variant="outline" onClick={() => setLocation(`/puzzles/custom/${puzzleId}`)}>
                {t("common.back")}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="content"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("puzzles.hintContent")}</FormLabel>
                      <FormControl>
                        <Textarea 
                          {...field} 
                          className="bg-zinc-800 border-zinc-700 min-h-[100px]" 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("puzzles.hintPrice")}</FormLabel>
                      <FormControl>
                        <Input 
                          type="number"
                          step="0.01"
                          min="1"
                          max="9999.99"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value))}
                          className="bg-zinc-800 border-zinc-700"
                        />
                      </FormControl>
                      <FormDescription>
                        {t("puzzles.hintPriceDescription")}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex gap-2">
                  <Button 
                    type="submit" 
                    disabled={createHintMutation.isPending || updateHintMutation.isPending}
                    className="flex-1"
                  >
                    {(createHintMutation.isPending || updateHintMutation.isPending) && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {editingHintId 
                      ? t("puzzles.updateHint") 
                      : t("puzzles.createHint")}
                  </Button>
                  
                  {editingHintId && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={cancelEditing}
                      className="flex-1"
                    >
                      {t("common.cancel")}
                    </Button>
                  )}
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle>{t("puzzles.existingHints")}</CardTitle>
          </CardHeader>
          <CardContent>
            {!hints || hints.length === 0 ? (
              <p className="text-zinc-400">{t("puzzles.noHints")}</p>
            ) : (
              <div className="space-y-4">
                {hints.map((hint) => (
                  <div 
                    key={hint.id} 
                    className="bg-zinc-800 border border-zinc-700 p-4 rounded-lg"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <p className="font-medium text-white">{t("puzzles.hintPrice")}: {formatNumber(hint.price)} cacatais</p>
                      <div className="flex space-x-2">
                        {/* <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingHintId(hint.id)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteHintMutation.mutate(hint.id)}
                          disabled={deleteHintMutation.isPending}
                        >
                          <Trash className="h-4 w-4 text-red-500" />
                        </Button> */}
                      </div>
                    </div>
                    <p className="text-zinc-300">{hint.content}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
